import { onScopeDispose, type Ref } from 'vue';

const SEARCH_PAUSE_MS = 300;

interface PausedSearchConfig {
    // The slot the search text lives in.
    keyword: Ref<string>;
    // What to do once the typing has paused: ask for the list under the text as it is by then.
    search: () => void;
    // Something to do as the text changes, before any request: drop a selection, say.
    onTyped?: () => void;
}

/**
 * A search box's keyups, turned into one search once the typing has paused.
 *
 * A search field reports every keyup, so the request waits until the typing stops. The text is
 * written to its slot at once, though — whatever shows that a filter is active sees it straight
 * away — and a keyup that leaves the text as it was (Shift, an arrow key) is not a search at all.
 *
 * `cancelSearch` drops a search still waiting on its pause: for a caller about to ask for the
 * list itself under the same text, and it is called when the owning scope goes away.
 *
 * useSearchableList is this and a paged list together; a list that fetches by hand (the guided
 * ancestry step) uses it on its own.
 */
export function usePausedSearch(config: PausedSearchConfig) {
    let searchTimer: ReturnType<typeof setTimeout> | null = null;

    const cancelSearch = () => {
        if (searchTimer) clearTimeout(searchTimer);
        searchTimer = null;
    };

    const onSearch = (event: any) => {
        const value = event?.target?.value ?? '';
        // A keyup that leaves the text as it was is not a search: nothing to fetch, and nothing
        // for onTyped to react to.
        if (value === config.keyword.value) return;
        config.keyword.value = value;
        config.onTyped?.();
        cancelSearch();
        searchTimer = setTimeout(() => {
            searchTimer = null;
            config.search();
        }, SEARCH_PAUSE_MS);
    };

    // failSilently: the composables built on this are also driven outside a component (their own
    // tests).
    onScopeDispose(cancelSearch, true);

    return { onSearch, cancelSearch };
}
